import React from "react";

/**
 * Shared Markdown Rendering Library
 * Used by reportCopilot and snowflakeCortexChat components
 */

/**
 * Convert a heading string to a URL-safe id for anchor links.
 * Strips citation markers like [key_name] before slugifying.
 */
export const slugify = (text) =>
  (text || "")
    .replace(/\[[^\]]*\]/g, "") // strip [citation_key] markers
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

/**
 * Render markdown as HTML string (for use with dangerouslySetInnerHTML)
 * Used by reportCopilot components
 */
export const renderMarkdownAsHTML = (text) => {
  if (!text) return "";

  let html = text
    // Escape HTML first
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    // Headers (process from most # to least to avoid partial matches)
    .replace(/^###### (.+)$/gm, "<h6>$1</h6>")
    .replace(/^##### (.+)$/gm, "<h6>$1</h6>")
    .replace(/^#### (.+)$/gm, "<h5>$1</h5>")
    .replace(/^### (.+)$/gm, "<h4>$1</h4>")
    .replace(/^## (.+)$/gm, "<h3>$1</h3>")
    .replace(/^# (.+)$/gm, "<h2>$1</h2>")
    // Bold
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Inline code
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    // Blockquotes (must run before line-break conversion)
    .replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>")
    // Line breaks
    .replace(/\n/g, "<br />");

  // Handle unordered lists (- items), including indented sub-lists
  html = html.replace(/(<br \/>)?(\s*- .+?)(?=<br \/>|$)/g, (match, br, item) => {
    const content = item.replace(/^\s*- /, "");
    return "<li>" + content + "</li>";
  });

  // Wrap consecutive list items in <ul>
  html = html.replace(/(<li>.*?<\/li>)+/g, "<ul>$&</ul>");

  // Handle numbered lists (1. items)
  html = html.replace(/(<br \/>)?(\d+\. .+?)(?=<br \/>|$)/g, (match, br, item) => {
    const content = item.replace(/^\d+\. /, "");
    return "<oli>" + content + "</oli>";
  });

  // Wrap consecutive ordered list items in <ol>, including sub-lists (<ul>)
  // Handle optional <br /> tags between items and sub-lists
  html = html.replace(/((?:<oli>.*?<\/oli>)(?:(?:\s*<br \/>\s*)*<ul>.*?<\/ul>)?(?:\s*<br \/>\s*)*)+/g, (match) => {
    let content = match.replace(/<oli>/g, "<li>").replace(/<\/oli>/g, "</li>");
    // Nest <ul> inside the preceding <li> for valid HTML structure
    content = content.replace(/<\/li>((?:\s*<br \/>\s*)*<ul>.*?<\/ul>)/g, "$1</li>");
    return "<ol>" + content + "</ol>";
  });

  // Clean up extra <br /> before/after lists
  html = html.replace(/<br \/><ul>/g, "<ul>");
  html = html.replace(/<\/ul><br \/>/g, "</ul>");
  html = html.replace(/<br \/><ol>/g, "<ol>");
  html = html.replace(/<\/ol><br \/>/g, "</ol>");

  return html;
};

/**
 * Render inline markdown text (bold, italic, etc.) as React elements
 * Used for rendering individual lines with markdown formatting
 */
export const renderMarkdownText = (text) => {
  if (!text) return null;

  // Split by bold markers (**text**)
  const parts = [];
  let lastIndex = 0;
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before the bold
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.substring(lastIndex, match.index) });
    }
    // Add the bold text
    parts.push({ type: "bold", content: match[1] });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.substring(lastIndex) });
  }

  if (parts.length === 0) {
    return text;
  }

  return parts.map((part, idx) => {
    if (part.type === "bold") {
      return <strong key={idx}>{part.content}</strong>;
    }
    return <span key={idx}>{part.content}</span>;
  });
};

/**
 * Strip inline markdown emphasis (bold/italic/code/strikethrough) from a table cell
 * so values like "**675,722**" are detected as numeric and export cleanly to Excel.
 */
export const stripInlineMarkdown = (cell) => {
  if (typeof cell !== "string") return cell;
  return cell
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/(^|[^*\w])\*([^*]+)\*(?!\w)/g, "$1$2")
    .trim();
};

/**
 * Strip emoji and symbol icons (arrows, geometric shapes, dingbats) from a cell.
 */
export const stripIcons = (cell) => {
  if (typeof cell !== "string") return cell;
  return cell
    .replace(/[\p{Extended_Pictographic}\u2190-\u21FF\u25A0-\u25FF\u2700-\u27BF\u2B00-\u2BFF\uFE0F\u200D]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

/**
 * Strip markdown and icons; used for numeric detection and for Excel/CSV export.
 */
export const stripCellForExport = (cell) => stripIcons(stripInlineMarkdown(cell));

/**
 * Parse markdown tables from text
 * Returns array of parts with type "text" or "table"
 * Captures column alignment from the separator row.
 */
export const parseMarkdownTable = (text) => {
  const tableRegex = /\|(.+)\|\n(\|[\s\-:|]+\|)\n((?:\|.+\|\n?)+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = tableRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex, match.index),
      });
    }

    const headerRow = match[1]
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell);

    // Parse alignment from separator row (e.g. |---|:---|---:|:---:|)
    const alignRow = match[2]
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell);
    const alignments = alignRow.map((cell) => {
      if (cell.startsWith(":") && cell.endsWith(":")) return "center";
      if (cell.endsWith(":")) return "right";
      return "left";
    });

    const bodyRows = match[3]
      .trim()
      .split("\n")
      .map((row) => {
        const cells = row.split("|").map((cell) => cell.trim());
        // Strip only the empty artifacts created by the leading/trailing pipe;
        // keep legitimately-empty interior cells so columns stay aligned. A
        // blanket .filter(Boolean) would drop empty cells and shift every
        // subsequent value left into the wrong column.
        if (cells.length && cells[0] === "") cells.shift();
        if (cells.length && cells[cells.length - 1] === "") cells.pop();
        return cells;
      });

    parts.push({
      type: "table",
      headers: headerRow,
      alignments,
      rows: bodyRows,
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.substring(lastIndex),
    });
  }

  return parts.length > 0 ? parts : [{ type: "text", content: text }];
};

/**
 * Render a parsed table part as a React <table> element.
 */
export const renderMarkdownTableElement = (tablePart, key) => {
  const { headers, alignments = [], rows } = tablePart;
  const getAlign = (i) => alignments[i] || "left";
  return (
    <table
      key={key}
      style={{
        borderCollapse: "collapse",
        width: "100%",
        margin: "12px 0",
        fontSize: "13px",
      }}>
      <thead>
        <tr>
          {headers.map((header, i) => (
            <th
              key={i}
              style={{
                border: "1px solid #d0d7de",
                padding: "6px 12px",
                background: "#f0f4fc",
                fontWeight: 600,
                textAlign: getAlign(i),
                whiteSpace: "nowrap",
              }}>
              {renderMarkdownText(header)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} style={{ background: ri % 2 === 0 ? "#fff" : "#f8f9fb" }}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                style={{
                  border: "1px solid #d0d7de",
                  padding: "5px 12px",
                  textAlign: getAlign(ci),
                }}>
                {renderMarkdownText(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * Render a single line of text with markdown support
 * Handles headings, lists, and inline formatting
 */
export const renderMarkdownLine = (line, lineIdx, renderTextFn) => {
  const renderFn = renderTextFn || renderMarkdownText;

  // Check if line is a header (starts with ###, ##, or #)
  if (line.startsWith("### ")) {
    const rawText = line.substring(4);
    return (
      <h3 key={lineIdx} id={slugify(rawText)} style={{ fontSize: "1.1em", fontWeight: "bold", margin: "10px 0 6px 0" }}>
        {renderFn(rawText)}
      </h3>
    );
  } else if (line.startsWith("## ")) {
    const rawText = line.substring(3);
    return (
      <h2 key={lineIdx} id={slugify(rawText)} style={{ fontSize: "1.2em", fontWeight: "bold", margin: "12px 0 8px 0" }}>
        {renderFn(rawText)}
      </h2>
    );
  } else if (line.startsWith("# ")) {
    const rawText = line.substring(2);
    return (
      <h1 key={lineIdx} id={slugify(rawText)} style={{ fontSize: "1.4em", fontWeight: "bold", margin: "14px 0 10px 0" }}>
        {renderFn(rawText)}
      </h1>
    );
  } else if (line.trim() === "---") {
    return <hr key={lineIdx} style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "12px 0" }} />;
  } else if (line.startsWith("> ")) {
    // Blockquote
    return (
      <blockquote
        key={lineIdx}
        style={{
          borderLeft: "3px solid #ccc",
          margin: "6px 0",
          paddingLeft: "10px",
          color: "#666",
          fontStyle: "italic",
        }}>
        {renderFn(line.substring(2))}
      </blockquote>
    );
  } else if (line.startsWith("- ")) {
    // Unordered list item
    return (
      <div key={lineIdx} style={{ marginLeft: "20px", marginBottom: "4px" }}>
        • {renderFn(line.substring(2))}
      </div>
    );
  } else if (/^\d+\.\s/.test(line)) {
    // Numbered list item (1. 2. 3. etc.)
    const match = line.match(/^(\d+)\.\s(.+)$/);
    if (match) {
      const number = match[1];
      const content = match[2];
      return (
        <div key={lineIdx} style={{ marginLeft: "20px", marginBottom: "4px" }}>
          {number}. {renderFn(content)}
        </div>
      );
    }
  } else if (line.trim() === "") {
    // Empty line - add spacing
    return <div key={lineIdx} style={{ height: "8px" }} />;
  } else {
    // Regular text with markdown
    return <div key={lineIdx}>{renderFn(line)}</div>;
  }
};

/**
 * Render full markdown content as React elements
 * Handles headings, lists, bold, tables, and line breaks
 */
export const renderMarkdownContent = (text) => {
  if (!text) return null;

  const parts = parseMarkdownTable(text);
  const elements = [];
  parts.forEach((part, partIdx) => {
    if (part.type === "table") {
      elements.push(renderMarkdownTableElement(part, `table-${partIdx}`));
    } else {
      part.content.split("\n").forEach((line, lineIdx) => {
        elements.push(renderMarkdownLine(line, `${partIdx}-${lineIdx}`, renderMarkdownText));
      });
    }
  });
  return <div>{elements}</div>;
};
